'use client';

import { useLocale, useTranslations } from '@dealerportal/i18n/client';
import { type FC, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import {
  FormatDate,
  getDateFormat,
  getMonths,
  getWeekDays,
} from '@dealerportal/utilities';
import { enUS, es, ptBR } from 'date-fns/locale';

interface Props {
  id: string;
  name: string;
  className: string;
  dates: string;
  isNFD: boolean;
  minDate: Date;
}

export const DatePickerInput: FC<Props> = (props) => {
  const t = useTranslations('ui');
  const localeCode = useLocale();
  const dates = JSON.parse(props?.dates ?? '{}');
  const datesArr = Object.keys(dates);
  const filterDates = (date: Date) => {
    const day = date.getDay().toString();
    return !datesArr.includes(day);
  };
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [DatePicker, setDatePicker] = useState<any>(null);
  const [disable, setDisable] = useState(false);
  const [isOpen, setIsOpen] = useState(true);

  const localeMap: Record<string, Locale> = {
    en: enUS,
    pt: ptBR,
    es: es,
  };
  const dateFnsLocale = localeMap[localeCode] || enUS;

  const weekDays = getWeekDays(localeCode as 'en' | 'pt' | 'es');
  const months = getMonths(localeCode as 'en' | 'pt' | 'es');
  const dateFormat = getDateFormat(localeCode as 'en' | 'pt' | 'es');

  return (
    <div>
      <input
        type="text"
        id={`${props.id}-temp`}
        name="temp-input"
        className={`${props.className} ${disable ? 'ui-hidden' : 'ui-block'}`}
        placeholder={t('preferredDate')}
        value={
          startDate
            ? FormatDate(
                startDate,
                dateFormat,
                localeCode as 'en' | 'pt' | 'es'
              )
            : ''
        }
        readOnly
        onClick={async (e) => {
          const module = await import('react-datepicker');
          module.registerLocale('en', enUS);
          module.registerLocale('pt', ptBR);
          module.registerLocale('es', es);
          setDatePicker(() => module.default);
          setDisable(true);
        }}
      />
      {DatePicker && (
        <div>
          <DatePicker
            locale={dateFnsLocale}
            dateFormat={dateFormat}
            id={props.id}
            name={props.name}
            className={props.className}
            selected={startDate}
            onChange={(date: Date) => {
              setStartDate(date);
            }}
            placeholderText={startDate ? undefined : t('preferredDate')}
            filterDate={filterDates}
            minDate={props?.minDate}
            open={isOpen}
            onClickOutside={() => {
              setIsOpen(false);
            }}
            onFocus={() => {
              setIsOpen(true);
            }}
            onInputClick={() => {
              setIsOpen(true);
            }}
            shouldCloseOnSelect={true}
            onSelect={(date: Date, event: React.SyntheticEvent<any>) => {
              setIsOpen(false);
            }}
            readOnly={props?.isNFD}
            weekDays={weekDays}
            months={months}
          />
        </div>
      )}
    </div>
  );
};

export default DatePickerInput;
